import { MemberSetup } from "../onboarding/MemberSetup";
import { BankStatementBookingProvider } from "../../components/BankStatementBookingProvider";
import mobileProductLogoDark from "../../../../../../branding/products/m1member/m1m-icon-dark-128.png";
import mobileProductLogoLight from "../../../../../../branding/products/m1member/m1m-icon-light-128.png";
import { themeRailBrightness } from "../../../../../../shared/frontend/theme/themeRailBrightness";
import { backgroundQueryRefresh } from "../../../../../../shared/frontend/system/backgroundQueryRefresh";
import { useDisplayZoom } from "../../../../../../shared/frontend/theme/displayZoom";
import { useDesktopChromeConfiguration } from "../desktop/useDesktopChromeConfiguration";
import { useAppNavigationWidth } from "../../../../../../shared/frontend/layout/useAppNavigationWidth";
import { AppShell, type AppShellMobileTab } from "../../../../../../shared/frontend/layout/AppShell";
import { useNavigationFooter } from "./useNavigationFooter";
import type { AppShellUserMenuProps } from "../../../../../../shared/frontend/layout/AppShellUserMenu";

import type { AppShellNavigation } from "../../../../../../shared/frontend/layout/appShellMenu";
import { Alert, Button, Grid, theme } from "../../../../../../shared/frontend/ui";
import React from "react";
import { Outlet, useLocation, useNavigate, useNavigationType } from "react-router-dom";

import { settingsBackGroupForPath, settingsListForEntryPath, settingsTitleForPath } from "../../pages/settings/settingsCatalog";
import { useConnectionAttentionCount } from "./useConnectionAttentionCount";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { PageMetadataProvider } from "../../../../../../shared/frontend/layout/PageMetadataProvider";
import { memberAdminCatalog } from "./adminNavigationCatalog";
import { adminNavigationEntry } from "../../../../../../shared/frontend/admin/adminNavigationCatalog";
import { toolsTitleForPath } from "../../pages/tools/toolsCatalog";
import { useAuth } from "../auth/useAuth";
import { layoutDefaults } from "../design-system";
import { ErrorBoundary } from "../errors/ErrorBoundary";
import { useI18n, useStoredLanguage } from "../i18n/useI18n";
import { LayoutBackButton } from "../navigation/LayoutBackButton";
import type { LayoutPageChrome } from "./LayoutPageChromeContext";
import { LayoutPageChromeProvider } from "./LayoutPageChromeProvider";
import {
  ADMIN_GENERAL_TITLE_BY_PATH,
  ADMIN_IMPORT_TITLE_BY_PATH,
  ADMIN_SETTINGS_TITLE_BY_PATH,
  resolvePageTitleKey,
} from "../pins/pinnablePage";
import { QeddyAssistant } from "../qeddy/QeddyAssistant";
import { useTheme } from "../theme/useTheme";
import { isM1DesktopShell } from "../desktop/m1Desktop";
import { ROUTE_ADMIN, ROUTE_ADMIN_IMPORT, ROUTE_ADMIN_IMPORT_ADAPTERS, ROUTE_ADMIN_IMPORT_MERGES, ROUTE_ADMIN_IMPORT_RUNS, ROUTE_ADMIN_LOGIN, ROUTE_ADMIN_SETTINGS, ROUTE_ROOT, ROUTE_SETTINGS_APPOINTMENT_CONFIGURATION, ROUTE_TOOLS } from "../constants/appRoutes";
import { PLATFORM_ADMIN_ROLE } from "../constants/authRole";
import { AUTH_ME_PATH } from "../api/tenantImportRequestPolicy";

const APPOINTMENT_CONFIGURATION_TAB_CALENDAR = "calendar";



import { ChangePasswordModal } from "./ChangePasswordModal";
import { useEnvironmentBanner } from "./EnvironmentModeBanner";
import { useTenantDeletionBanner } from "../tenantDeletion/useTenantDeletionBanner";
import { useStudioLockBanner } from "../studios/useStudioLockBanner";
import { LockedContentFrame } from "../../../../../../shared/frontend/layout/LockedContentFrame";
import { useStudioContextConfiguration } from "./GlobalStudioSelector";
import { useGlobalStudioContext } from "../studios/useGlobalStudioContext";
import { InfoModal } from "./InfoModal";
import { LayoutImportRunHeadingContext } from "./LayoutImportRunHeadingContext";
import { LayoutPagePinContext } from "./LayoutPagePinContext";
import { useCurrentPagePinConfiguration } from "./PinCurrentPageButton";
import { usePinnedLinksConfiguration } from "./PinnedLinksBar";
import type { MenuRoute } from "./menu";
import { buildMenu, computeMenuSelection, findMenuRouteByPath, menuRoutesToAntdItems } from "./menu";
import { QuickSearchDialog } from "./QuickSearchDialog";
import { useOpenTaskCount } from "./useOpenTaskCount";
import type { PinnedLink } from "../pins/pinnedLinks";

import { useMenuCollapsedStorageKeys } from "./useMenuCollapsed";
import { useAppShellNavigationCollapsed } from "../../../../../../shared/frontend/layout/useAppShellNavigationCollapsed";
import { useFeedback } from "../../../../../../shared/frontend/feedback/useFeedback";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRealtimeQueryBridge } from "../query/useRealtimeQueryBridge";
import { globalKey } from "../query/queryKeys";
import { useRealtimeListRefresh } from "../realtime/RealtimeContext";
import { RealtimeEventTypes } from "../realtime/realtimeEventTypes";
import type { CriticalIssueDto } from "../../../../../../shared/frontend/feedback/GlobalCriticalIssuesBanner";
import { authQueryScope } from "../query/authQueryScope";

/** Menü- und Routenpfad des Erlebnisformulars — Verfügbarkeitsprüfung und Eintrag müssen ihn teilen. */
const SIGNING_EXPERIENCE_PATH = "/sales/signing";

// Operative Hauptwege; Rechte, Modulfreigaben, Texte und Symbole kommen aus buildMenu.
const TASKS_PATH = "/tasks";
const MOBILE_PRIMARY_PATHS = ["/persons", "/calendar", "/checkin", TASKS_PATH] as const;



function collectMenuRoutes(items: MenuRoute[], depth = 0): Array<{ name: string; depth: number; hasChildren: boolean }> {
  return items.flatMap((item) => [
    { name: item.name, depth, hasChildren: Boolean(item.children?.length) },
    ...(item.children ? collectMenuRoutes(item.children, depth + 1) : [])
  ]);
}

export function AppLayout() {
  const auth = useAuth();
  const feedback = useFeedback();
  const themeCtx = useTheme();

  const studioContext = useStudioContextConfiguration();
  const { token } = theme.useToken();

  React.useLayoutEffect(() => {
    const color = token.colorBgLayout;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prevHtml = html.style.backgroundColor;
    const prevBody = body.style.backgroundColor;
    const prevRoot = root?.style.backgroundColor ?? "";
    html.style.backgroundColor = color;
    body.style.backgroundColor = color;
    if (root) root.style.backgroundColor = color;
    return () => {
      html.style.backgroundColor = prevHtml;
      body.style.backgroundColor = prevBody;
      if (root) root.style.backgroundColor = prevRoot;
    };
  }, [token.colorBgLayout]);
  const screens = Grid.useBreakpoint();
  const { text, tr, lang } = useI18n();
  const environmentBanner = useEnvironmentBanner(tr);
  const tenantDeletionBanner = useTenantDeletionBanner(tr);
  const studioLock = useStudioLockBanner(tr);
  const [, setStoredLang] = useStoredLanguage();
  const loc = useLocation();
  const navigationType = useNavigationType();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useAppShellNavigationCollapsed(useMenuCollapsedStorageKeys());
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const isMobile = screens.md === false;
  const menu = React.useMemo(() => buildMenu(auth.info, text), [auth.info, text]);

  const normalizePath = React.useCallback((p: string) => {
    const s = (p || "").trim();
    if (!s) return "";
    if (s.length > 1 && s.endsWith("/")) return s.slice(0, -1);
    return s;
  }, []);

  const effectivePathname = React.useMemo(() => {
    const p = normalizePath(loc.pathname || "");
    if (p && p !== ROUTE_ROOT) return p;
    try {
      const h = String(window.location.hash || "");
      if (h.startsWith("#/")) return normalizePath(h.slice(1));
      if (h.startsWith("#")) return normalizePath(h.slice(1));
    } catch {
      // ignore (non-browser env)
    }
    return p || "/";
  }, [loc.pathname, normalizePath]);

  const signingExperienceRoute = React.useMemo(
    () => findMenuRouteByPath(menu, SIGNING_EXPERIENCE_PATH),
    [menu]
  );
  useRealtimeQueryBridge("lookups");
  const { studiosLoadFailed, refetchStudios } = useGlobalStudioContext();
  const queryClient = useQueryClient();
  React.useEffect(() => auth.api.subscribeWrites(path => {
    const studioAccessChanged = /^\/api\/v1\/(studios|users|user-roles)(\/|$)/.test(path);
    if (/^\/api\/v1\/studios(?:\/[^/]+)?$/.test(path)) {
      // Eine Statusänderung verändert auch „alle Studios“ und bereits gecachte Fachdaten.
      void queryClient.invalidateQueries();
      return;
    }
    if (studioAccessChanged || path === AUTH_ME_PATH) {
      void queryClient.invalidateQueries({ queryKey: globalKey("lookups", "studios-with-access") });
    }
    if (studioAccessChanged || /^\/api\/v1\/(contract-signing-widgets|contract-package-templates)(\/|$)/.test(path)) {
      void queryClient.invalidateQueries({ queryKey: globalKey("lookups", "signing-availability") });
    }
  }), [auth, queryClient]);
  const signingQuery = useQuery({
    queryKey: globalKey("lookups", "signing-availability", ...authQueryScope(auth.api, auth.info)),
    queryFn: ({ signal }) => auth.api.get<{ available?: boolean; reason?: string | null }>("/api/v1/contract-signing/availability", { signal, suppressGlobalErrorToast: true }),
    enabled: Boolean(signingExperienceRoute) && !auth.info?.tenantImportCommitInProgress,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: query => backgroundQueryRefresh(query, "always"),
    retry: false
  });
  const signingAvailability = signingQuery.isPending ? "checking" : signingQuery.isError || signingQuery.data?.available !== true ? "unavailable" : "available";
  const signingUnavailableReason = signingQuery.isError ? null : signingQuery.data?.reason ?? null;
  const showAdminCriticalIssues =
    (auth.info?.role || "") === PLATFORM_ADMIN_ROLE &&
    (effectivePathname === ROUTE_ADMIN || effectivePathname.startsWith("/admin/"));

  // Der Hinweis über dem Adminbereich ruft über den ApiClient ab (Token im Kopf) und folgt den
  // Live-Ereignissen der Betriebsseite; der Takt des Bausteins bleibt das Sicherheitsnetz.
  const [criticalIssuesSignal, setCriticalIssuesSignal] = React.useState(0);
  useRealtimeListRefresh({
    eventTypes: [RealtimeEventTypes.FailureCircuitsChanged],
    reload: () => setCriticalIssuesSignal(value => value + 1),
    enabled: showAdminCriticalIssues && !!auth.token,
  });
  const loadCriticalIssues = React.useCallback(
    async () => await auth.api.get<CriticalIssueDto[]>("/api/v1/admin/critical-issues", { suppressGlobalErrorToast: true }) ?? [],
    [auth.api],
  );
  const openCriticalIssueTarget = React.useCallback((target: string) => nav(`/admin/operations?tab=${encodeURIComponent(target)}`), [nav]);

  const menuWidthRoutes = React.useMemo(() => collectMenuRoutes(menu), [menu]);

  const siderWidth = useAppNavigationWidth(menuWidthRoutes);

  const topLevelKeySet = React.useMemo(
    () => new Set(menu.map((r) => normalizePath(r.path)).filter(Boolean)),
    [menu, normalizePath]
  );

  const routeMenuState = React.useMemo(
    () => computeMenuSelection(effectivePathname, menu),
    [effectivePathname, menu]
  );

  /** Eingeklappt: keine route-basierten openKeys — sonst zeigt Ant Menu Untermenüs als Popup („Hover“) direkt nach Reload. */
  const [openKeysOverride, setOpenKeysOverride] = React.useState<string[]>(() =>
    collapsed ? [] : routeMenuState.openKeys
  );

  const prevCollapsedRef = React.useRef(collapsed);
  React.useLayoutEffect(() => {
    const was = prevCollapsedRef.current;
    if (was !== collapsed) {
      if (collapsed) {
        setOpenKeysOverride([]);
      } else {
        setOpenKeysOverride(routeMenuState.openKeys);
      }
      prevCollapsedRef.current = collapsed;
    }
  }, [collapsed, routeMenuState.openKeys]);

  const selectedKeys = routeMenuState.selectedKeys;
  const openKeys = openKeysOverride;

  const openKeysRef = React.useRef(openKeys);
  openKeysRef.current = openKeys;

  /** Eingeklappt: Untermenüs per Klick öffnen (s. triggerSubMenuAction), nicht zum Hub navigieren. */
  const handleTopLevelSubmenuTitleClick = React.useCallback(
    (path: string) => {
      if (collapsed) return;
      if (!openKeysRef.current.includes(path)) {
        nav(path);
      }
    },
    [nav, collapsed]
  );

  const hasTasksEntry = menu.some(item => item.path === TASKS_PATH);
  const openTaskCount = useOpenTaskCount(hasTasksEntry);
  const tasksAttention = React.useMemo(
    () => openTaskCount > 0 ? { tone: "attention" as const, label: text("Open tasks"), count: openTaskCount } : undefined,
    [openTaskCount, text]
  );

  const baseMenuItems = React.useMemo(
    () => menuRoutesToAntdItems(menu, { onTopLevelSubmenuTitleClick: handleTopLevelSubmenuTitleClick }),
    [menu, handleTopLevelSubmenuTitleClick]
  );
  // Angehaltene Anbindungen (SMS, WhatsApp): Markierung an der Einstellungsgruppe, die die Integrationen führt.
  const connectionAttentionCount = useConnectionAttentionCount();
  const connectionAttention = React.useMemo(
    () => connectionAttentionCount > 0
      ? { tone: "attention" as const, label: text("Connections on hold"), count: connectionAttentionCount }
      : undefined,
    [connectionAttentionCount, text]
  );
  const menuItems = React.useMemo(() => {
    const integrationsGroupKey = settingsBackGroupForPath("/settings/integrations")?.key;
    const integrationsGroupPath = integrationsGroupKey ? `/settings/group/${integrationsGroupKey}` : null;
    const withConnectionAttention = (items: typeof baseMenuItems): typeof baseMenuItems => items.map(item => {
      if (item.key === integrationsGroupPath && connectionAttention) return { ...item, attention: connectionAttention };
      return item.children ? { ...item, children: withConnectionAttention(item.children) } : item;
    });
    const withTasks = tasksAttention ? baseMenuItems.map(item => item.key === TASKS_PATH ? { ...item, attention: tasksAttention } : item) : baseMenuItems;
    return connectionAttention ? withConnectionAttention(withTasks) : withTasks;
  }, [baseMenuItems, tasksAttention, connectionAttention]);

  const mobileNavigationItems = React.useMemo<AppShellMobileTab[]>(
    () => MOBILE_PRIMARY_PATHS.flatMap(path => {
      const route = menu.find(item => item.path === path && !item.hideInMenu);
      if (!route?.icon) return [];
      return [{ key: route.path, label: route.name, icon: route.icon, ...(path === TASKS_PATH && tasksAttention ? { attention: tasksAttention } : {}) }];
    }),
    [menu, tasksAttention]
  );

  const onOpenChange: AppShellNavigation["onOpenChange"] = React.useCallback(
    (keys: string[]) => {
      const next = keys.map(String);
      const prev = openKeysRef.current;
      if (next.length > prev.length) {
        const added = next.filter((k: string) => !prev.includes(k));
        const addedTop = added.filter((k: string) => topLevelKeySet.has(k));
        if (addedTop.length > 0) {
          setOpenKeysOverride([addedTop[addedTop.length - 1]]);
          return;
        }
      }
      setOpenKeysOverride(next);
    },
    [topLevelKeySet]
  );

  const navigateFromMenu = React.useCallback(
    (k: string) => {
      const node = findMenuRouteByPath(menu, k);
      /*
        Das Erlebnisformular laeuft jetzt in der Anwendung. Der Hinweis bleibt trotzdem: Ohne
        konfiguriertes Angebot fuehrt der Aufruf sonst auf eine leere Seite, deren Grund erst der
        Server kennt.
      */
      if (k === SIGNING_EXPERIENCE_PATH && signingAvailability !== "available") {
        feedback.info(
          signingAvailability === "checking"
            ? text("The signing experience is still being checked. Please try again in a moment.")
            // Der Grund kommt vom Server; ohne ihn bleibt der allgemeine Hinweis.
            : signingUnavailableReason
              ? text(signingUnavailableReason)
              : text("The signing experience is not configured yet. Configure at least one active offer before opening it.")
        );
        setMobileMenuOpen(false);
        return;
      }
      nav(k);
      setMobileMenuOpen(false);
      if (topLevelKeySet.has(k)) {
        if (!node?.children?.length) {
          setOpenKeysOverride([]);
        }
      }
    },
    [feedback, nav, signingAvailability, signingUnavailableReason, text, topLevelKeySet, menu]
  );

  const onMenuClick: AppShellNavigation["onClick"] = React.useCallback(
    ({ key }) => navigateFromMenu(String(key)),
    [navigateFromMenu]
  );

  /** Fachlicher Pin der aktuellen Seite (z. B. Personenakte mit Name und Foto). */
  const [pagePin, setPagePin] = React.useState<PinnedLink | null>(null);
  const pagePinContextValue = React.useMemo(() => ({ setPagePin }), []);
  const [pageChrome, setPageChrome] = React.useState<LayoutPageChrome | null>(null);
  const pageChromeContextValue = React.useMemo(() => ({ setPageChrome }), []);

  const [changePasswordModalOpen, setChangePasswordModalOpen] = React.useState(false);
  const [infoModalOpen, setInfoModalOpen] = React.useState(false);

  const displayName = auth.info?.email || auth.info?.displayName || text("User");
  const tenant = auth.info?.tenantName || auth.info?.tenantId || "";
  const showToolsHeader = loc.pathname.startsWith("/tools/") && loc.pathname !== ROUTE_TOOLS;
  const showAdminSettingsHeader = loc.pathname.startsWith("/admin/settings/") && loc.pathname !== ROUTE_ADMIN_SETTINGS;
  const showAdminImportHeader =
    loc.pathname.startsWith("/admin/import/") && loc.pathname !== ROUTE_ADMIN_IMPORT;

  /** Admin: Karten vom Admin-Dashboard – gleicher Kopf wie bei Admin-Einstellungen/Import (Zurück + Titel). */
  const showAdminGeneralSubPage =
    effectivePathname.startsWith("/admin/") &&
    effectivePathname !== ROUTE_ADMIN &&
    effectivePathname !== ROUTE_ADMIN_LOGIN &&
    !(
      effectivePathname.startsWith("/admin/settings/") ||
      (effectivePathname.startsWith("/admin/import/") && effectivePathname !== ROUTE_ADMIN_IMPORT)
    );

  const showBackButton = pageChrome?.hideBackButton !== true;

  const adminSettingsTitleByPath = ADMIN_SETTINGS_TITLE_BY_PATH;
  const adminImportTitleByPath = ADMIN_IMPORT_TITLE_BY_PATH;
  const adminGeneralTitleByPath = ADMIN_GENERAL_TITLE_BY_PATH;

  const [importRunPageHeading, setImportRunPageHeading] = React.useState<string | null>(null);
  React.useEffect(() => {
    setImportRunPageHeading(null);
  }, [effectivePathname]);

  const settingsTitle = React.useMemo(() => {
    if (showAdminImportHeader) {
      if (
        (loc.pathname.startsWith("/admin/import/runs/") && loc.pathname !== ROUTE_ADMIN_IMPORT_RUNS) ||
        (loc.pathname.startsWith("/admin/import/adapters/") && loc.pathname !== ROUTE_ADMIN_IMPORT_ADAPTERS) ||
        (loc.pathname.startsWith("/admin/import/merges/") && loc.pathname !== ROUTE_ADMIN_IMPORT_MERGES)
      ) {
        return importRunPageHeading ?? text("Import run page heading bare");
      }
      const adminImportTitle = adminImportTitleByPath[loc.pathname];
      return adminImportTitle ? text(adminImportTitle) : null;
    }
    if (showAdminSettingsHeader) {
      const adminTitle = adminSettingsTitleByPath[loc.pathname] ?? adminNavigationEntry(memberAdminCatalog, loc.pathname)?.titleKey;
      return adminTitle ? text(adminTitle) : null;
    }
    if (showAdminGeneralSubPage) {
      const adminGeneralTitle = adminGeneralTitleByPath[effectivePathname];
      return adminGeneralTitle ? text(adminGeneralTitle) : null;
    }
    if (loc.pathname === ROUTE_SETTINGS_APPOINTMENT_CONFIGURATION) {
      const params = new URLSearchParams(loc.search || "");
      const tab = String(params.get("tab") || "");
      if (tab === APPOINTMENT_CONFIGURATION_TAB_CALENDAR) return text("Calendar configuration");
      return text("Calendar");
    }
    if (showToolsHeader) {
      const toolsTitle = toolsTitleForPath(loc.pathname);
      if (toolsTitle) return tr(toolsTitle);
    }
    // Ein Listeneintrag (`/settings/<liste>/<eintrag>`) behält den Titel seiner Liste.
    const base = settingsTitleForPath(loc.pathname) ?? settingsListForEntryPath(loc.pathname)?.titleKey;
    return base ? tr(base) : null;
  }, [
    loc.pathname,
    loc.search,
    effectivePathname,
    showAdminSettingsHeader,
    showAdminImportHeader,
    showAdminGeneralSubPage,
    showToolsHeader,
    text,
    tr,
    importRunPageHeading
  ]);

  const catalogTitleKey = resolvePageTitleKey(effectivePathname);
  const catalogPageTitle = settingsTitle ?? (catalogTitleKey ? text(catalogTitleKey) : null)
    ?? findMenuRouteByPath(menu, effectivePathname)?.name;

  const handleLanguageChange = React.useCallback(
    async (lang: "de" | "en") => {
      setStoredLang(lang);
      try {
        await auth.api.updateMe({ language: lang });
        await auth.refreshMe();
      } catch (e) {
        console.error(text("Error while changing language:"), e);
      }
    },
    [auth, setStoredLang, text]
  );

  const base = import.meta.env.BASE_URL ?? "";
  const logoShort = `${base}images/mq-64x35.png`;
  const isDark = themeCtx.theme.brightness === "dark";

  // Gleicher Speicherschlüssel wie die frühere m1member-Anzeigegröße: gewählte Größen bleiben erhalten.
  const [displayZoom, setDisplayZoom] = useDisplayZoom(`m1member.zoom.${auth.info?.accountId || "anon"}`);
  const userMenu: AppShellUserMenuProps = {
      displayName,
      openLabel: text("Open user account menu"),
      identity: { contextLabel: text("Tenant"), context: tenant },
      userSettings: { label: text("Navigation.account"), onClick: () => nav("/account") },
      theme: { label: text("Theme.menu"), tr: text },
      zoom: { label: text("Theme.zoom"), value: displayZoom, onChange: setDisplayZoom },
      language: { value: lang, onChange: language => { void handleLanguageChange(language); } },
      changePassword: { label: text("Change password"), onClick: () => setChangePasswordModalOpen(true) },
      information: { label: text("Information"), onClick: () => setInfoModalOpen(true) },
      billing: auth.hasAnyPermission("billing.read", "billing.write") ? { onClick: () => nav("/billing") } : undefined,
      logs: auth.info?.role === PLATFORM_ADMIN_ROLE
        ? { label: text("System Logs"), onClick: () => nav("/admin/logs") }
        : auth.hasAnyPermission("logs.read") ? { label: text("Logs"), onClick: () => nav("/logs") } : undefined,
      logout: { label: text("Log out"), onClick: () => { auth.logout(); nav("/login"); } }
  };
  const [quickSearchOpen, setQuickSearchOpen] = React.useState(false);
  const openQuickSearch = React.useCallback(() => {
    setMobileMenuOpen(false);
    setQuickSearchOpen(true);
  }, [setMobileMenuOpen]);
  const quickSearch = React.useMemo(
    () => ({
      label: text("Quick search"),
      shortcutHint: /mac/i.test(navigator.platform) ? "⌘K" : text("Ctrl+K"),
      onOpen: openQuickSearch
    }),
    [openQuickSearch, text]
  );
  const homePath = auth.info?.role === PLATFORM_ADMIN_ROLE ? ROUTE_ADMIN : auth.hasAnyPermission("dashboard.view") ? "/overview" : "/";
  const brandHome = { label: text("Navigation.overview"), onClick: () => nav(homePath) };
  const inDesktopShell = isM1DesktopShell();
  const desktopConfiguration = useDesktopChromeConfiguration();
  const pinnedPages = usePinnedLinksConfiguration({ menu });
  const pinAction = useCurrentPagePinConfiguration({ menu, path: effectivePathname, pagePin });
  const navigationFooter = useNavigationFooter();

  return (
    <BankStatementBookingProvider key={`${auth.info?.tenantId}:${auth.info?.accountId}`}>
    <QeddyAssistant onOpen={isMobile ? () => setMobileMenuOpen(false) : undefined}>
      <><AppShell brand={{ name: tr("m1member"), logo: logoShort, compactLogo: themeRailBrightness(themeCtx.theme) === "dark" ? mobileProductLogoDark : mobileProductLogoLight, home: brandHome }}
        contentLocation={{ key: loc.key, pathname: loc.pathname, action: navigationType }}
        navigation={{ items: menuItems, selectedKeys, openKeys, onOpenChange, onClick: onMenuClick }}
        navigationWidth={siderWidth}
        collapsed={collapsed}
        onCollapsedChange={setCollapsed}
        mobile={isMobile}
        mobileNavigationOpen={mobileMenuOpen}
        onMobileNavigationOpenChange={setMobileMenuOpen}
        mobileTabs={mobileNavigationItems.length ? {
          items: mobileNavigationItems,
          selectedKey: selectedKeys[0],
          onSelect: navigateFromMenu,
          menuLabel: text("Menu.navigation")
        } : undefined}
        labels={{ expandNavigation: text("Expand main sidebar"), collapseNavigation: text("Collapse main sidebar") }}
        navigationFooter={navigationFooter}
        pinnedPages={pinnedPages}
        pinAction={pinAction}
        context={studioContext}
        search={quickSearch}
        userMenu={userMenu}
        help
        criticalBanner={showAdminCriticalIssues ? { loadIssues: loadCriticalIssues, reloadSignal: criticalIssuesSignal, onOpenTarget: openCriticalIssueTarget } : undefined}
        banners={[tenantDeletionBanner, studioLock.banner, environmentBanner].filter((banner): banner is NonNullable<typeof banner> => Boolean(banner))}
        desktop={inDesktopShell ? desktopConfiguration : undefined}
        contentStyle={layoutDefaults.appContentStyle}
        contentWidth={pageChrome?.fullWidth ? "full" : "default"}>{<>
          {studiosLoadFailed ? <Alert type="error" message={text("Error loading studios")} action={<Button onClick={refetchStudios}>{text("Retry")}</Button>} /> : null}
          {showBackButton ? <LayoutBackButton /> : null}
        </>}
        <ErrorBoundary>
          <React.Suspense key={loc.pathname} fallback={<LoadingOverlay visible scope="placeholder" />}>
            <LayoutImportRunHeadingContext.Provider value={{ setImportRunPageHeading }}>
              <LayoutPageChromeProvider value={pageChromeContextValue}>
                <LayoutPagePinContext.Provider value={pagePinContextValue}>
                  <PageMetadataProvider value={pageChrome?.hidePageHeader ? null : { title: catalogPageTitle, extra: pageChrome?.headerExtra }}>
                    <LockedContentFrame locked={studioLock.framed}><MemberSetup><Outlet /></MemberSetup></LockedContentFrame>
                  </PageMetadataProvider>
                </LayoutPagePinContext.Provider>
              </LayoutPageChromeProvider>
            </LayoutImportRunHeadingContext.Provider>
          </React.Suspense>
        </ErrorBoundary>
      </AppShell>{<>
        <ChangePasswordModal open={changePasswordModalOpen} onClose={() => setChangePasswordModalOpen(false)} />
        <InfoModal open={infoModalOpen} onClose={() => setInfoModalOpen(false)} isDark={isDark} />
        <QuickSearchDialog open={quickSearchOpen} onClose={() => setQuickSearchOpen(false)} menu={menu} />
      </>}</>
    </QeddyAssistant>
    </BankStatementBookingProvider>
  );
}
